import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Chip,
  Box,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  ExitToApp,
  Menu as MenuIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '../RoleBasedRoute';

interface HeaderProps {
  title?: string;
  onMenuToggle?: () => void;
  showMenuButton?: boolean;
}

export const Header: React.FC<HeaderProps> = ({
  title = 'お問い合わせ画面',
  onMenuToggle,
  showMenuButton = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { adminUser, isAdmin } = usePermissions();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    // ログアウト処理
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    sessionStorage.clear();
    navigate('/login');
    handleMenuClose();
    setTimeout(() => { window.location.reload(); }, 100);
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'admin': return '管理者';
      case 'operator': return '運営者';
      default: return role;
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin': return 'error' as const;
      case 'operator': return 'primary' as const;
      default: return 'default' as const;
    }
  };

  return (
    <AppBar 
      position="fixed" 
      sx={{ 
        zIndex: theme.zIndex.drawer + 1,
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        border: 'none',
      }}
    >
      <Toolbar sx={{ minHeight: 60, px: 2 }}>
        {/* 左側：メニューボタン + ロゴ + タイトル */}
        <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, gap: 1.5 }}>
          {showMenuButton && (
            <IconButton
              edge="start"
              color="inherit"
              aria-label="menu"
              onClick={onMenuToggle}
              sx={{ mr: 1 }}
            >
              <MenuIcon />
            </IconButton>
          )}
          
          {/* ロゴ */}
          <Box
            component="img"
            src="/logo-horizontal.png"
            alt="MyTalk SYSTEM"
            sx={{
              height: isMobile ? '36px' : '44px',
              width: 'auto',
            }}
          />
          
          <Typography 
            variant="h6" 
            component="div" 
            sx={{ 
              fontWeight: 600,
              color: theme.palette.text.primary,
              fontSize: isMobile ? '1.1rem' : '1.25rem',
            }}
          >
            {title}
          </Typography>
        </Box>

        {/* 右側：ユーザーメニュー */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 1 }}>
            {!isMobile && (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                  {adminUser?.displayName || 'ユーザー'}
                </Typography>
                <Chip
                  label={getRoleLabel(adminUser?.role || 'user')}
                  color={getRoleColor(adminUser?.role || 'user')}
                  size="small"
                  sx={{ 
                    height: 20, 
                    fontSize: '0.7rem',
                    '& .MuiChip-label': { px: 1 }
                  }}
                />
              </Box>
            )}
            
            <IconButton
              color="inherit"
              onClick={handleMenuOpen}
              sx={{ p: 0.5 }}
            >
              <Avatar
                sx={{ 
                  width: 36, 
                  height: 36,
                  backgroundColor: theme.palette.primary.main,
                  fontSize: '0.9rem',
                }}
              >
                {adminUser?.displayName?.charAt(0) || 'U'}
              </Avatar>
            </IconButton>
          </Box>
        </Box>

        {/* ユーザーメニュー */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          PaperProps={{ sx: { mt: 1, minWidth: 200, boxShadow: theme.shadows[8] } }}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          {isMobile && (
            <Box sx={{ px: 2, py: 1, border: 'none' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {adminUser?.displayName || 'ユーザー'}
              </Typography>
              <Chip
                label={getRoleLabel(adminUser?.role || 'user')}
                color={getRoleColor(adminUser?.role || 'user')}
                size="small"
                sx={{ mt: 0.5 }}
              />
            </Box>
          )}

          <MenuItem onClick={handleLogout}>
            <ExitToApp sx={{ mr: 1 }} />
            ログアウト
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
};

export default Header;




